import React, { useEffect, useState } from "react";
import axios from "axios";

const ExceptionReportsView = () => {
  const [reports, setReports] = useState([]);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [type, setType] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const fetchReports = async () => {
    const token = localStorage.getItem("accessToken");
    if (!token) {
      setError("Authentication token missing.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const res = await axios.get("/api/actionables/exception-reports/", {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          from_date: fromDate || undefined,
          to_date: toDate || undefined,
          type: type || undefined,
        },
      });

      setReports(res.data || []);
    } catch (err) {
      console.error(err);
      setError("Failed to load exception reports.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
  }, [fromDate, toDate, type]);

  return (
    <div className="pageWrapper">
      <h1 className="pageTitle">Exception Reports</h1>

      {/* FILTERS */}
      <div className="filterCard">
        <div className="filterItem">
          <label>From Date</label>
          <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
        </div>

        <div className="filterItem">
          <label>To Date</label>
          <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
        </div>

        <div className="filterItem">
          <label>Exception Type</label>
          <select value={type} onChange={(e) => setType(e.target.value)}>
            <option value="">All</option>
            <option value="Operation">Operational</option>
            <option value="Technical">Technical</option>
            <option value="Other">Other</option>
          </select>
        </div>
      </div>

      {/* STATUS */}
      {loading && <p className="infoText">Loading reports…</p>}
      {error && <p className="errorText">{error}</p>}

      {/* TABLE */}
      {!loading && reports.length > 0 && (
        <div className="tableCard">
          <div className="tableWrapper">
            <table className="modernTable">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>User</th>
                  <th>Exception Type</th>
                  <th>Actionable No.</th>
                  <th>Report</th>
                  <th>File</th>
                </tr>
              </thead>
              <tbody>
                {reports.map((r) => (
                  <tr key={r.id}>
                    <td>{new Date(r.created_at).toLocaleDateString()}</td>
                    <td>{r.username}</td>
                    <td>
                      <span className={`pill pill-${r.type?.toLowerCase()}`}>
                        {r.type}
                      </span>
                    </td>
                    <td>
                      {Array.isArray(r.actionable_number)
                        ? r.actionable_number.join(", ")
                        : "-"}
                    </td>
                    <td className="reportCell">{r.report}</td>
                    <td>
                      {r.images ? (
                        <a href={r.images} target="_blank" rel="noreferrer" className="link">
                          View
                        </a>
                      ) : (
                        "—"
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!loading && reports.length === 0 && (
        <p className="infoText">No exception reports found.</p>
      )}

      {/* STYLES */}
      <style>{`
        .pageWrapper {
          padding: 40px;
          background: #f5f7fb;
          min-height: 100vh;
          font-family: Inter, sans-serif;
        }

        .pageTitle {
          font-size: 28px;
          font-weight: 700;
          color: #0f1d60;
          margin-bottom: 25px;
        }

        /* FILTERS */
        .filterCard {
          background: white;
          padding: 20px;
          border-radius: 16px;
          display: flex;
          gap: 20px;
          flex-wrap: wrap;
          margin-bottom: 25px;
          box-shadow: 0 6px 18px rgba(0,0,0,0.08);
        }

        .filterItem {
          display: flex;
          flex-direction: column;
          min-width: 180px;
          gap: 6px;
          font-size: 14px;
        }

        .filterItem input,
        .filterItem select {
          padding: 10px;
          border-radius: 10px;
          border: 1px solid #d1d5db;
        }

        /* TABLE */
        .tableCard {
          background: white;
          border-radius: 16px;
          box-shadow: 0 8px 25px rgba(0,0,0,0.1);
          padding: 20px;
        }

        .tableWrapper {
          overflow-x: auto;
        }

        .modernTable {
          width: 100%;
          border-collapse: collapse;
        }

        .modernTable th {
          background: #0f1d60;
          color: white;
          padding: 12px;
          text-align: left;
          font-size: 14px;
        }

        .modernTable td {
          padding: 12px;
          border-bottom: 1px solid #e5e7eb;
          vertical-align: top;
          font-size: 14px;
        }

        .modernTable tr:hover {
          background: #f2f6ff;
        }

        .reportCell {
          max-width: 320px;
          white-space: pre-wrap;
          line-height: 1.5;
        }

        /* STATUS PILLS */
        .pill {
          padding: 4px 10px;
          border-radius: 999px;
          font-size: 12px;
          font-weight: 600;
          text-transform: capitalize;
        }

        .pill-operation {
          background: #dbeafe;
          color: #1e40af;
        }

        .pill-technical {
          background: #fee2e2;
          color: #991b1b;
        }

        .pill-other {
          background: #fef3c7;
          color: #92400e;
        }

        /* TEXT */
        .infoText {
          margin-top: 20px;
          font-size: 15px;
          color: #374151;
        }

        .errorText {
          color: #b91c1c;
          font-weight: 600;
          margin-top: 10px;
        }

        .link {
          color: #2563eb;
          font-weight: 600;
          text-decoration: none;
        }

        .link:hover {
          text-decoration: underline;
        }
      `}</style>
    </div>
  );
};

export default ExceptionReportsView;
